import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, GitBranch, Users, Settings, Radar, LogOut, Eye, EyeOff, ClipboardCheck, ListChecks, FolderCheck, MessagesSquare, FileText, GitCompareArrows, Library, ServerCog, FileSignature, FolderOpen, FileBadge, Camera, Briefcase } from "lucide-react";
import { Logo } from "@/components/Logo";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";
import { Footer } from "@/components/Footer";

export function Layout({ children, viewAsSales, setViewAsSales }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const effectiveRole = isAdmin && viewAsSales ? "sales" : user?.role;

  const showAdmin = isAdmin && !viewAsSales;
  const navItems = [
    { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/contractor-finder", label: "DoD Contractor Finder", icon: Radar, testid: "nav-sam" },
    ...(showAdmin
      ? [
          { to: "/scoring-logic", label: "Scoring Logic", icon: GitBranch, testid: "nav-scoring-logic" },
          { to: "/users", label: "User Access", icon: Users, testid: "nav-users" },
          { to: "/settings", label: "Admin Settings", icon: Settings, testid: "nav-settings" },
        ]
      : []),
  ];
  const opsItems = showAdmin
    ? [
        { to: "/operations/engagements", label: "Customer Engagements", icon: Briefcase, testid: "nav-engagements" },
        { to: "/operations/reconciliation", label: "Implementation Reconciliation", icon: ClipboardCheck, testid: "nav-recon" },
        { to: "/operations/tasks", label: "Reconciliation Tasks", icon: ListChecks, testid: "nav-recon-tasks" },
        { to: "/operations/evidence", label: "Evidence Requests", icon: FolderCheck, testid: "nav-recon-evidence" },
        { to: "/operations/questions", label: "Questions Workspace", icon: MessagesSquare, testid: "nav-recon-questions" },
        { to: "/operations/reports", label: "Readiness Reports", icon: FileText, testid: "nav-recon-reports" },
        { to: "/operations/compare", label: "Compare Versions", icon: GitCompareArrows, testid: "nav-recon-compare" },
        { to: "/operations/evidence-blueprints", label: "Evidence Blueprint Library", icon: Library, testid: "nav-blueprints" },
        { to: "/operations/tech-profile", label: "Customer Technology Profile", icon: ServerCog, testid: "nav-tech-profile" },
        { to: "/operations/policy-generator", label: "Policy & Procedure Generator", icon: FileSignature, testid: "nav-policy-generator" },
        { to: "/operations/customer-documents", label: "Customer Documents", icon: FolderOpen, testid: "nav-customer-documents" },
        { to: "/operations/ssp", label: "SSP Builder", icon: FileBadge, testid: "nav-ssp-builder" },
        { to: "/operations/ssp-library", label: "SSP Library", icon: Library, testid: "nav-ssp-library" },
        { to: "/operations/environment-evidence", label: "Environment Evidence", icon: Camera, testid: "nav-environment-evidence" },
      ]
    : [];

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-navy text-white flex-col hidden md:flex fixed h-screen z-20">
        <div className="flex flex-col items-center justify-center gap-2 px-4 py-6 border-b border-white/10">
          <Logo variant="light" className="h-24 w-auto" />
          <div className="text-[10px] text-gold tracking-[0.2em] uppercase">Command Center</div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-6 space-y-1 [scrollbar-width:thin] [scrollbar-color:rgba(255,255,255,0.2)_transparent]">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-white/10 text-gold" : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <item.icon className="h-4 w-4" aria-hidden="true" />
              {item.label}
            </NavLink>
          ))}

          {opsItems.length > 0 && (
            <div className="pt-5">
              <div className="px-3 pb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500">Operations</div>
              {opsItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                      isActive ? "bg-white/10 text-gold" : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <item.icon className="h-4 w-4" aria-hidden="true" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        {isAdmin && (
          <div className="px-4 pb-4">
            <button
              data-testid="view-as-toggle"
              onClick={() => setViewAsSales((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium bg-white/5 text-slate-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              {viewAsSales ? <EyeOff className="h-3.5 w-3.5" aria-hidden="true" /> : <Eye className="h-3.5 w-3.5" aria-hidden="true" />}
              {viewAsSales ? "Viewing as Sales User" : "View as Sales User"}
            </button>
          </div>
        )}

        <div className="px-4 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-gold/20 flex items-center justify-center text-gold text-sm font-semibold">
                {(user?.name || "?")[0]}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-gold">{effectiveRole}</div>
            </div>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 min-w-0 flex flex-col min-h-screen">
        <DisclaimerBanner />
        <div className="flex-1">{children}</div>
        <Footer />
      </div>
    </div>
  );
}
